import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import shoppingCartIcon from "../../assets/icons/checkout.png";
import magnifyingGlass from "../../assets/icons/search.png";
import Button from "../../components/Button/Button";
import Modal from "../../components/Modal";
import StoreCard from "../../components/StoreCard";
import "./shopping.scss";

const Shopping = ({ storeData }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [storeTab, setStoreTab] = useState("Everything");
  const [activeProduct, setActiveProduct] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [sortType, setSortType] = useState("alphabetical");
  const [showOnlyAvailable, setShowOnlyAvailable] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const numItemsInCart = useSelector(
    (state) => state.storeSlice.numItemsInCart,
  );
  const isMobile = useSelector((state) => state.appSlice.isMobile);

  const isInShoppingSection =
    location.pathname === "/store" || location.pathname === "/store/";

  const [footerNearView, setFooterNearView] = useState(false);

  useEffect(() => {
    const footer = document.getElementById("footer");
    if (!footer) return;

    let unstickTimeout = null;
    let restickTimeout = null;
    const UNSTICK_DELAY = 80;
    const RESTICK_DELAY = 250;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          restickTimeout && clearTimeout(restickTimeout);
          restickTimeout = null;
          unstickTimeout = setTimeout(
            () => setFooterNearView(true),
            UNSTICK_DELAY,
          );
        } else {
          unstickTimeout && clearTimeout(unstickTimeout);
          unstickTimeout = null;
          restickTimeout = setTimeout(
            () => setFooterNearView(false),
            RESTICK_DELAY,
          );
        }
      },
      {
        rootMargin: "0px 0px 180px 0px",
        threshold: 0,
      },
    );
    observer.observe(footer);
    return () => {
      observer.disconnect();
      unstickTimeout && clearTimeout(unstickTimeout);
      restickTimeout && clearTimeout(restickTimeout);
    };
  }, []);

  // FROM GOOGLE SHEETS
  //~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  const fruitTrees = useMemo(
    () => ({
      "Apple Trees": storeData["fruit trees"]["apple"].flatMap((item) => item),
      "Pear Trees": storeData["fruit trees"]["pear"].flatMap((item) => item),
      "Peach and Nectarine Trees": storeData["fruit trees"]["peach"].flatMap(
        (item) => item,
      ),
      "Sour Cherry Trees": storeData["fruit trees"]["sour cherry"].flatMap(
        (item) => item,
      ),
      "Sweet Cherry Trees": storeData["fruit trees"]["sweet cherry"].flatMap(
        (item) => item,
      ),
      "Apricot Trees": storeData["fruit trees"]["apricot"].flatMap(
        (item) => item,
      ),
      "Plum Trees": storeData["fruit trees"]["plum"].flatMap((item) => item),
    }),
    [storeData],
  );
  const nutOthers = storeData["nut & other trees"].flatMap((item) => item);
  const perennialBerryBushes = storeData["perennials berries & bushes"].flatMap(
    (item) => item,
  );
  const rootstock = storeData["rootstock"].flatMap((item) => item);
  const supplies = storeData["supplies"].flatMap((item) => item);
  const allTrees = [
    ...Object.values(fruitTrees),
    ...nutOthers,
    ...perennialBerryBushes,
    ...rootstock,
    ...supplies,
  ].flatMap((item) => item);
  // sort products given the list of products and [alphabetically or price]
  function sortProducts(products, sortType) {
    return products.sort((a, b) => {
      // First, sort by availability (inStock items first)
      if (a.inStock && !b.inStock) return -1;
      if (!a.inStock && b.inStock) return 1;

      // Then, sort by the specified sortType
      if (sortType === "alphabetical") {
        return a.title.localeCompare(b.title);
      } else if (sortType === "priceLowHigh") {
        return a.price - b.price;
      }

      return 0;
    });
  }
  // Helper function to group items by variety and create product objects
  const groupItemsByVariety = useCallback((items) => {
    const grouped = {};

    items.forEach((item) => {
      const variety = item.Variety;
      if (!grouped[variety]) {
        grouped[variety] = [];
      }
      grouped[variety].push(item);
    });

    return grouped;
  }, []);

  // Helper function to create product objects with multiple sizes
  const createProductObject = useCallback((variety, items) => {
    const sanitizedVariety = variety
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");

    let imagePath = `${process.env.PUBLIC_URL}/store-images/${sanitizedVariety}.png`;

    //!All haskaps have same image
    if (variety.toLowerCase().includes("haskap")) {
      imagePath = `./store-images/haskap.png`;
    }

    if (
      variety.toLowerCase().includes("wild") &&
      variety.toLowerCase().includes("buckthorn")
    ) {
      imagePath = `./store-images/lord-sea-buckthorn.png`;
    }
    if (
      variety.toLowerCase().includes("english") &&
      variety.toLowerCase().includes("walnut")
    ) {
      imagePath = `./store-images/english-walnut.png`;
    }

    // Sort items by price to show cheapest first (create copy to avoid mutation)
    const sortedItems = [...items].sort(
      (a, b) => parseFloat(a.Price) - parseFloat(b.Price),
    );

    return new Product(
      variety,
      sortedItems, // Pass array of items instead of single item
      imagePath,
    );
  }, []);

  // Memoize sorted and filtered products
  const filteredAndSortedProducts = useMemo(() => {
    let productList =
      storeTab === "Everything"
        ? allTrees
        : storeTab === "Nut and Other Tree Seedlings"
          ? nutOthers
          : storeTab === "Perennials, Berries and Bushes"
            ? perennialBerryBushes
            : storeTab === "Supplies"
              ? supplies
              : storeTab === "Rootstock"
                ? rootstock
                : allTrees;

    if (searchTerm) {
      productList = productList.filter(
        (item) =>
          item.Variety.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (item.description &&
            item.description.toLowerCase().includes(searchTerm.toLowerCase())),
      );
    }

    if (showOnlyAvailable) {
      productList = productList.filter((item) => item.Inventory > 0);
    }

    // Group items by variety and create Product objects
    const groupedItems = groupItemsByVariety(productList);
    const products = Object.entries(groupedItems).map(([variety, items]) => {
      return createProductObject(variety, items);
    });

    return sortProducts(products, sortType);
  }, [
    storeTab,
    searchTerm,
    showOnlyAvailable,
    sortType,
    allTrees,
    nutOthers,
    perennialBerryBushes,
    supplies,
    rootstock,
    createProductObject,
    groupItemsByVariety,
  ]);

  // Memoize callback functions
  const handleCardClick = useCallback((productInfo) => {
    setActiveProduct(productInfo);
  }, []);

  const handleSortChange = useCallback((event) => {
    setSortType(event.target.value);
  }, []);

  // Simplified generateCards that uses our memoized list
  const generateCards = useCallback(() => {
    return filteredAndSortedProducts.map((product, index) => (
      <StoreCard
        key={index}
        productInfo={product}
        onCardClick={handleCardClick}
      />
    ));
  }, [filteredAndSortedProducts, handleCardClick]);

  // Modified generateFruitCards that uses the same pattern
  const generateFruitCards = useCallback(() => {
    return Object.entries(fruitTrees)
      .map(([key, value]) => {
        if (!value || !value.length) return null;

        const filteredValue = value.filter((item) => {
          if (showOnlyAvailable && item.Inventory <= 0) return false;
          if (
            searchTerm &&
            !item.Variety.toLowerCase().includes(searchTerm.toLowerCase())
          )
            return false;
          return true;
        });

        if (!filteredValue.length) return null;

        // Group filtered items by variety
        const groupedItems = groupItemsByVariety(filteredValue);
        const products = Object.entries(groupedItems).map(
          ([variety, items]) => {
            return createProductObject(variety, items);
          },
        );

        return (
          <div key={key} className="fruit-basket">
            <div>
              <h2 className="basket-title">{key}</h2>
            </div>
            <div className="cards-container">
              {products.map((product, index) => {
                return (
                  <StoreCard
                    key={`${key}-${index}`}
                    productInfo={product}
                    onCardClick={handleCardClick}
                  />
                );
              })}
            </div>
          </div>
        );
      })
      .filter(Boolean); // Remove null entries
  }, [
    fruitTrees,
    showOnlyAvailable,
    searchTerm,
    handleCardClick,
    groupItemsByVariety,
    createProductObject,
  ]);

  // const removeUnavailable = (productList) => {
  //   return productList.filter((item) => item.Inventory > 0);
  // };
  const handleAvailableChange = () => {
    setShowOnlyAvailable(!showOnlyAvailable);
  };
  const handleTabClick = (tabName) => {
    setStoreTab(tabName);
  };
  const handleCloseModal = () => {
    setActiveProduct(null); // Clear the active product to close the modal
  };
  const handleSearchTermChange = (event) => {
    setSearchTerm(event.target.value);
  };

  return (
    <div
      className={`shopping-container ${
        isMobile && isInShoppingSection && !footerNearView
          ? "sticky-sidebar-active"
          : ""
      }`}
    >
      <div
        className={`store-sidebar ${
          isMobile && isInShoppingSection && !footerNearView
            ? "sticky-sidebar"
            : ""
        }`}
      >
        <div className="sidebar-top">
          <div className="store-categories">
            <Button
              className="dropdown-btn"
              variant="secondary"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              {storeTab} &nbsp;&nbsp; {isDropdownOpen ? "▲" : "▼"}
            </Button>
            <ul className={`category-list ${isDropdownOpen ? "open" : ""}`}>
              {[
                "Everything",
                "Fruit Trees",
                "Nut and Other Tree Seedlings",
                "Perennials, Berries and Bushes",
                "Rootstock",
                "Supplies",
              ].map((tabName) => (
                <li
                  key={tabName}
                  className={storeTab === tabName ? "active" : ""}
                  onClick={() => handleTabClick(tabName)}
                >
                  {tabName}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="sidebar-bottom">
          <Button
            className="checkout-button"
            onClick={() => navigate("review-cart")}
          >
            <img
              className="__checkout"
              src={shoppingCartIcon}
              alt="shopping cart icon"
            />
            <h3>Cart</h3>
            <p className="updating-cart-items">{numItemsInCart}</p>
          </Button>
        </div>
      </div>
      <div className="store-content">
        <div className="content-header-container">
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder="Search ..."
              value={searchTerm}
              onChange={handleSearchTermChange}
            ></input>
            <button className="search-icon">
              <img
                className="__search"
                src={magnifyingGlass}
                alt="magnifying glass icon"
              />
            </button>
          </div>
          <div className="filter-container">
            <div
              className="available-container"
              onClick={handleAvailableChange}
            >
              <div
                className={`toggle-switch ${showOnlyAvailable ? "active" : ""}`}
              >
                <div className="toggle-slider"></div>
              </div>
              <label htmlFor="availableItems">In Stock Items Only</label>
            </div>
            <div className="sort-container">
              <select
                className="sort-select"
                name="sort"
                onChange={handleSortChange}
                defaultValue=""
              >
                <option value="alphabetical">A-Z</option>
                <option value="priceLowHigh">Price Low to High</option>
              </select>
            </div>
          </div>
        </div>
        <div className="cards-container">
          {storeTab !== "Fruit Trees" && generateCards()}
        </div>
        {storeTab === "Fruit Trees" && generateFruitCards()}
        {activeProduct && (
          <Modal product={activeProduct} onClose={handleCloseModal} />
        )}
      </div>
    </div>
  );
};

export default memo(Shopping);

// class of Products to send to store card creator
class Product {
  constructor(
    title,
    items, // Array of items with different sizes
    imagePath = null,
  ) {
    this.title = title;
    this.imagePath = imagePath;
    this.items = items; // Array of all size variants

    // Set properties based on the first (cheapest) item for display
    const firstItem = items[0];
    this.price = firstItem.Price;
    this.inStock = firstItem.Inventory > 0;
    this.description = firstItem.Description;
    this.size = firstItem.Size;
    this.rootstock = firstItem.Rootstock;
    this.pollination = firstItem.Pollination;
    this.form = firstItem.Form;

    // Check if any variant is in stock
    this.hasAnyInStock = items.some((item) => item.Inventory > 0);

    // Get all unique sizes for dropdown
    this.availableSizes = items.map((item) => ({
      size: item.Size,
      price: item.Price,
      inventory: item.Inventory,
      inStock: item.Inventory > 0,
      rootstock: item.Rootstock,
      pollination: item.Pollination,
      form: item.Form,
      description: item.Description,
    }));
  }

  // Method to get item details for a specific size
  getItemForSize(size) {
    return this.items.find((item) => item.Size === size) || this.items[0];
  }
}
